import { AntTag, Progress, useHelmet } from '@agro-club/agroclub-shared'
import { Bid, BidStatus } from 'modules/domain/bid/types'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { AButton } from 'views/components/Analytics'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import { BidStatusTag, BidTypeTag } from 'views/components/Tags'
import { CreatedUpdatedTag } from 'views/components/Tags/CreatedUpdatedTag'
import { TaskModal } from 'views/components/TaskModal/TaskModal'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import * as Layout from 'views/layouts/NewLayout'
import BidRoutes from 'views/pages/Bid/routes'
import * as Header from 'views/ui/Header/Header'
import { AnalyticsPages, AnalyticsPlaces } from '../../analyticsPlaces'
import { BidPageTitle } from '../styled'
import { ArchiveBidButton } from './ArchiveBidButton'
import { BarchartIcon } from './BarchartIcon'
import { BidLeftBlock } from './BidLeftBlock'
import { BidRightBlock } from './BidRightBlock'
import { SampleWithRequests } from 'views/pages/Samples/types'
import { ExportBotWrapper } from 'views/pages/Samples/SampleDetail/ExportBotWrapper'
import useFormatDate from 'hooks/useFormatDate'
import { PublishToLocalWrapper } from 'views/pages/Samples/SampleDetail/PublishToLocalWrapper'
import { useAProgress } from 'hooks/useAProgress'
import { apiClient } from 'modules/utils/httpClient'

export const BidDetail = () => {
  const { id } = useParams()
  const { t } = useTranslation(['bid', 'common', 'samples'])
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [fetchProgress, bid, refreshBid] = useSingleEntity<Bid>(endpoints.bid(id))
  const [, samples, samplesRefetch] = useSingleEntity<SampleWithRequests[]>(endpoints.samplesForBid(id))
  useHelmet({ title: `${t('common:bid')} - ${bid?.product?.title || id}` })
  const formatDate = useFormatDate()

  const [progress, actualiseBid] = useAProgress(
    async () => {
      await apiClient.post(endpoints.actualiseBid(id))
      await refreshBid()
    },
    {
      eventName: 'actualise',
      scope: 'change',
    },
  )

  if (fetchProgress === Progress.WORK || !bid || !samples) {
    return <ItemLoadingLayout id={id} />
  }
  const latest_sample = samples[0]

  return (
    <APageWrapper page={AnalyticsPages.BID_DETAIL} place={AnalyticsPlaces.DETAIL} id={id}>
      <Layout.WrapperContent>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                { path: BidRoutes.List, breadcrumbName: t('list.heading') },
                { breadcrumbName: bid.product?.title },
              ]}
            />
            <Header.ButtonWrapper>
              {latest_sample && (
                <>
                  <PublishToLocalWrapper sample={latest_sample} onSucess={samplesRefetch} />
                  <ExportBotWrapper sample={latest_sample} onSucess={samplesRefetch} />
                </>
              )}
              <AButton
                id="a"
                color="secondary"
                onClick={actualiseBid}
                size="medium"
                progress={progress}
                disabled={bid.status !== BidStatus.published}
              >
                {t('actualiseBid')}
              </AButton>
              <AButton id="setTask" color="secondary" onClick={() => setIsOpen(true)} size="medium">
                {t('common:setTask')}
              </AButton>

              <Header.LineSeparator />

              <ArchiveBidButton bid={bid} />
            </Header.ButtonWrapper>
          </Layout.TopHeader>
          <Layout.PageTitleWithTagWrapper>
            <BidPageTitle>{bid.product?.title}</BidPageTitle>
            <BarchartIcon source={bid.source} />
            <BidTypeTag bid={bid} />
            <BidStatusTag bid={bid} />
            <CreatedUpdatedTag instance={bid} />
            {latest_sample?.sent_to_export_bot_date && (
              <AntTag color="magenta">{`${t('samples:sentToExportBot')}: ${formatDate(
                latest_sample.sent_to_export_bot_date,
              )}`}</AntTag>
            )}
            {latest_sample?.sent_to_local_bot_date && (
              <AntTag color="cyan">{`${t('samples:sentToLocalBot')}: ${formatDate(
                latest_sample.sent_to_local_bot_date,
              )}`}</AntTag>
            )}
          </Layout.PageTitleWithTagWrapper>
        </Layout.Header>
        <Layout.ContentTwoColumns>
          <div>
            <BidLeftBlock bid={bid} refreshBid={refreshBid} />
          </div>
          <div>
            <BidRightBlock bid={bid} samples={samples} samplesRefetch={samplesRefetch} />
          </div>
        </Layout.ContentTwoColumns>
      </Layout.WrapperContent>
      {isOpen && <TaskModal user={bid.owner} bid={bid} close={() => setIsOpen(false)} />}
    </APageWrapper>
  )
}
